/**
 * LinkEditPlugin - 链接编辑插件（悬停编辑模式）
 *
 * 功能：
 * - 鼠标悬停链接时显示"编辑"按钮
 * - 点击编辑按钮将节点替换为可编辑的 inline input
 * - 输入 /link 插入新的可编辑链接
 * - 保持在文本流中
 *
 * 实现方式：
 * - 使用 mouseenter/mouseleave 事件
 * - 不修改 Lexical 管理的 DOM 结构
 * - 使用 fixed 定位的编辑按钮（放在 body 中）
 */

import { $isLinkNode } from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createParagraphNode, $getNearestNodeFromDOMNode, $getRoot, $getSelection, $insertNodes, $isRangeSelection } from "lexical";
import { useEffect, useRef } from "react";
import { $createEditableLinkNode } from "../nodes/EditableLinkNode";

export function LinkEditPlugin(): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const linksRef = useRef<Set<HTMLAnchorElement>>(new Set());
  const buttonsRef = useRef<Map<HTMLAnchorElement, HTMLButtonElement>>(new Map());

  // 暴露创建方法到 window（供 SlashCommand 调用）
  useEffect(() => {
    (window as any).editor = editor;

    (window as any).openLinkCreateDialog = () => {
      editor.update(() => {
        const editableLinkNode = $createEditableLinkNode("", "");
        $insertNodes([editableLinkNode]);

        const paragraphNode = $createParagraphNode();
        $insertNodes([paragraphNode]);
        paragraphNode.select(0, 0);
      });
    };
    return () => {
      delete (window as any).openLinkCreateDialog;
      delete (window as any).editor;
    };
  }, [editor]);

  // 监听链接并添加事件
  useEffect(() => {
    const rootElement = editor.getRootElement();
    if (!rootElement) return;

    const setupLink = (linkElement: HTMLAnchorElement) => {
      if (linksRef.current.has(linkElement)) return;

      const linkHref = linkElement.getAttribute("href") || linkElement.href;

      linksRef.current.add(linkElement);

      let editBtn: HTMLButtonElement | null = null;
      let hideTimeout: number | null = null;

      const cleanupButton = () => {
        if (editBtn && editBtn.parentElement) {
          editBtn.remove();
        }
        editBtn = null;
        buttonsRef.current.delete(linkElement);
      };

      const showButton = () => {
        if (editBtn) return;

        editBtn = document.createElement("button");
        editBtn.innerHTML = "✎";
        editBtn.title = "编辑链接";
        editBtn.type = "button";

        // 使用内联样式设置所有样式，避免 CSS 冲突
        Object.assign(editBtn.style, {
          position: "fixed",
          width: "28px",
          height: "28px",
          fontSize: "12px",
          cursor: "pointer",
          borderRadius: "4px",
          backgroundColor: "hsl(var(--primary))",
          color: "hsl(var(--primary-foreground))",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          zIndex: "9999",
          pointerEvents: "auto",
          userSelect: "none",
          webkitUserSelect: "none",
          transition: "opacity 0.15s, transform 0.15s",
          opacity: "1",
        });

        // 添加 hover 效果
        editBtn.addEventListener("mouseenter", () => {
          if (editBtn) {
            editBtn.style.transform = "scale(1.1)";
            if (hideTimeout) {
              clearTimeout(hideTimeout);
              hideTimeout = null;
            }
          }
        });
        editBtn.addEventListener("mouseleave", () => {
          if (editBtn) editBtn.style.transform = "scale(1)";
        });

        // 计算位置
        const updatePosition = () => {
          const rect = linkElement.getBoundingClientRect();
          editBtn!.style.top = `${rect.top + rect.height / 2 - 14}px`;
          editBtn!.style.left = `${rect.right + 8}px`;
        };

        updatePosition();

        // 添加到 body
        document.body.appendChild(editBtn);
        buttonsRef.current.set(linkElement, editBtn);

        // ✅ 点击编辑按钮：通过 DOM 选择获取链接对应的节点
        editBtn.addEventListener("click", (e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();

          // ✅ 方法：直接在 update() 中遍历查找
          editor.update(() => {
            // 优先根据当前 hover 的 DOM 元素定位对应 LinkNode，避免 URL 相同命中第一个链接。
            let foundLinkNode: any = null;

            const nearestNode = $getNearestNodeFromDOMNode(linkElement);
            if (nearestNode) {
              if ($isLinkNode(nearestNode)) {
                foundLinkNode = nearestNode;
              } else {
                let parent = nearestNode.getParent();
                while (parent) {
                  if ($isLinkNode(parent)) {
                    foundLinkNode = parent;
                    break;
                  }
                  parent = parent.getParent();
                }
              }
            }

            // 回退方案：当 DOM 映射失败时，再按 URL 遍历查找。
            if (!foundLinkNode) {
              const root = $getRoot();
              if (!root) {
                cleanupButton();
                return;
              }

              const traverse = (node: any) => {
                if (!node || foundLinkNode) return;

                if ($isLinkNode(node)) {
                  const nodeUrl = node.getURL();
                  if (nodeUrl === linkHref || nodeUrl === linkElement.href) {
                    foundLinkNode = node;
                    return;
                  }
                }

                const children = node.getChildren ? node.getChildren() : [];
                for (const child of children) {
                  traverse(child);
                  if (foundLinkNode) return;
                }
              };

              traverse(root);
            }

            if (!foundLinkNode) {
              cleanupButton();
              return;
            }

            const url = foundLinkNode.getURL();
            const text = foundLinkNode.getTextContent();
            const selection = $getSelection();
            const shouldRepairSelection = $isRangeSelection(selection);

            const editableNode = $createEditableLinkNode(url, text !== url ? text : "");
            const parent = foundLinkNode.getParent();
            const offsetAfterNode = foundLinkNode.getIndexWithinParent() + 1;
            foundLinkNode.replace(editableNode);

            // Keep selection valid after replacing a potentially selected LinkNode.
            if (shouldRepairSelection) {
              if (parent) {
                parent.select(offsetAfterNode, offsetAfterNode);
              } else {
                editableNode.selectNext();
              }
            }
          });

          cleanupButton();
        });
      };

      const hideButton = () => {
        if (hideTimeout) clearTimeout(hideTimeout);
        hideTimeout = window.setTimeout(() => {
          if (editBtn && editBtn.matches(":hover")) return;
          cleanupButton();
        }, 200);
      };

      // ✅ 在外部添加事件监听器（不在 showButton 内部）
      linkElement.addEventListener("mouseenter", showButton);
      linkElement.addEventListener("mouseleave", hideButton);
      linkElement.addEventListener("click", cleanupButton);
    };

    // 初始设置所有链接
    const links = rootElement.querySelectorAll("a");
    links.forEach((link) => {
      if (link instanceof HTMLAnchorElement) {
        setupLink(link);
      }
    });

    // 监听新链接
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLAnchorElement) {
            setupLink(node);
          } else if (node instanceof Element) {
            const links = node.querySelectorAll("a");
            links.forEach((link) => {
              if (link instanceof HTMLAnchorElement) {
                setupLink(link);
              }
            });
          }
        });

        mutation.removedNodes.forEach((node) => {
          if (node instanceof HTMLAnchorElement) {
            linksRef.current.delete(node);
            const btn = buttonsRef.current.get(node);
            if (btn) btn.remove();
            buttonsRef.current.delete(node);
          }
        });
      });
    });

    observer.observe(rootElement, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      linksRef.current.forEach((link) => {
        link.removeEventListener("mouseenter", () => {});
        link.removeEventListener("mouseleave", () => {});
        link.removeEventListener("click", () => {});
      });
      linksRef.current.clear();
      buttonsRef.current.forEach((btn) => btn.remove());
      buttonsRef.current.clear();
    };
  }, [editor]);

  return <></>;
}

export default LinkEditPlugin;
